import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import { Permission, PermissionProject, SubscriptionType, Team, TeamInfo, User } from 'models';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';
import { TeamInviteCodeHelpers } from 'helpers/TeamInviteCodeHelpers';
import { TeamUserHelpers } from 'helpers/TeamUserHelpers';

const TeamHelpers = {
    urls: {
        requestAccess: (teamId: string) => `/teams/${teamId}/request-access`,
        grantAccess: (teamId: string) => `/teams/${teamId}/grant-access`,
        settings: (team?: CollectionOrId<Team> | null) => {
            if (!team) {
                return '/account';
            }

            const id = CollectionHelpers.id(team);

            return `/account#team-${id}`;
        },
        creditUsage: (teamId?: string) => (teamId ? `/account?tab=usage#team-${teamId}` : '/account?tab=usage'),
        managePlan: (teamId?: string) => (teamId ? `/account?tab=plan#team-${teamId}` : '/account?tab=plan'),
        invite: (team: Team) => `/teams/${team.id}/join?code=${TeamInviteCodeHelpers.encode(team.secret)}`,
    },

    isPartOfTeam: (team: Team | null, user: User | User['id'] | null) => {
        return CollectionHelpers.contains(TeamUserHelpers.fromTeam(team), user, 'user');
    },

    getCreditBalance: (team?: Team | null) => {
        return team?.creditBalance || 0;
    },

    getUserPermissions: (team: Team | null, user: User | User['id'] | null) => {
        return TeamUserHelpers.getPermissions(CollectionHelpers.find(TeamUserHelpers.fromTeam(team), user, 'user'));
    },

    getUserRole: (team: Team, user: User) => {
        return TeamUserHelpers.getRole(CollectionHelpers.find(TeamUserHelpers.fromTeam(team), user, 'user'));
    },

    hasUserPermission: (team: Team | null, user: User | User['id'] | null, permission: Permission | Permission[]) => {
        return TeamUserHelpers.hasPermission(
            CollectionHelpers.find(TeamUserHelpers.fromTeam(team), user, 'user'),
            permission,
        );
    },

    canMoveProject: (team: Team | null, user: User | null, userTeams: readonly TeamInfo[]) => {
        const hasTargetTeam = userTeams.some(
            (userTeam) =>
                userTeam.id !== team?.id &&
                userTeam.permissions.some(
                    (permission) => permission === PermissionProject.ALL || permission === PermissionProject.EDIT,
                ),
        );

        return (
            TeamHelpers.hasUserPermission(team, user, PermissionProject.ADMIN) && (hasTargetTeam || !!user?.internal)
        );
    },

    getDesignerSubscription: (team?: Team | null) => {
        return SubscriptionHelpers.getSubscriptionData(SubscriptionType.DESIGNER, team?.subscriptions).subscription;
    },
};

export { TeamHelpers };
